import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import Logo from "../components/Logo";
import ProfileMenu from "../components/ProfileMenu";
import {
  TrendingUp, TrendingDown, Minus, ArrowRight, ArrowLeft, GitCompare,
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, AlertTriangle,
} from "lucide-react";

const RC = (s) => s >= 70 ? "var(--au-error)" : s >= 40 ? "var(--au-warn)" : "var(--au-accent-ink)";
const SEVHEX = { critical: "var(--au-error)", high: "var(--au-warn)", medium: "var(--au-accent-ink)", low: "var(--au-success)", info: "var(--au-muted)" };

// Risk over time — inline SVG line chart (theme-token stroke, no deps)
function RiskLine({ series }) {
  const pts = (series || []).filter(s => s.risk_score != null);
  if (pts.length < 2) return <div style={{ color: "var(--au-muted)", fontSize: 13 }}>Run at least two scans to see a trend line.</div>;
  const W = 720, H = 160, PAD = 24;
  const xs = (i) => PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
  const ys = (v) => H - PAD - (v / 100) * (H - 2 * PAD);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)},${ys(p.risk_score).toFixed(1)}`).join(" ");
  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 460, height: "auto" }} role="img" aria-label="Risk score over time">
        {[0, 25, 50, 75, 100].map(g => (
          <g key={g}>
            <line x1={PAD} x2={W - PAD} y1={ys(g)} y2={ys(g)} stroke="var(--au-hair)" strokeWidth="1" />
            <text x={4} y={ys(g) + 3} fontSize="9" fill="var(--au-muted)">{g}</text>
          </g>
        ))}
        <path d={path} fill="none" stroke="var(--au-accent)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={xs(i)} cy={ys(p.risk_score)} r="3.5" fill={RC(p.risk_score)}>
            <title>{`${p.target} — ${p.risk_score} (${new Date(p.scanned_at).toLocaleDateString()})`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

function DeltaBadge({ delta }) {
  if (delta == null) return <span style={{ fontSize: 11, color: "var(--au-muted)" }}>—</span>;
  // Risk went DOWN = good (green); UP = bad (red)
  const good = delta < 0, flat = delta === 0;
  const Icon = flat ? Minus : good ? TrendingDown : TrendingUp;
  const clr = flat ? "var(--au-muted)" : good ? "var(--au-success)" : "var(--au-error)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: clr }}>
      <Icon size={13} /> {delta > 0 ? "+" : ""}{delta}
    </span>
  );
}

export default function Trends() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [cmp, setCmp] = useState(null);       // comparison result
  const [cmpTarget, setCmpTarget] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);

  const load = async () => {
    setError(false); setBusy(true);
    try {
      const r = await api.get("/api/scan/trends");
      setData(r.data);
    } catch (e) {
      setError(true); toast.error("Couldn't load trends");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const compare = async (t) => {
    if (!t.previous_scan_id) { toast.error("Need two scans of this target to compare"); return; }
    setCmpBusy(true); setCmpTarget(t.target); setCmp(null);
    try {
      const r = await api.get(`/api/scan/compare/${t.previous_scan_id}/${t.latest_scan_id}`);
      setCmp(r.data);
    } catch (e) {
      toast.error("Comparison failed");
    } finally { setCmpBusy(false); }
  };

  if (busy) return <div className="au-root" style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", color: "var(--au-accent-ink)", fontFamily: "var(--au-font-mono)" }}><Loader2 size={28} className="au-spin" /><span>Loading…</span></div>;

  if (error) return (
    <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="au-glass au-pad-lg" style={{ textAlign: "center", maxWidth: 420 }}>
        <AlertTriangle size={40} style={{ color: "var(--au-warn)", marginBottom: 14 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--au-text)", marginBottom: 8 }}>Couldn't load trends</h2>
        <button onClick={load} className="au-pill au-pill-primary au-pill-sm"><RefreshCw size={14} /> Retry</button>
      </div>
    </div>
  );

  const targets = data?.targets || [];
  const series = data?.series || [];

  return (
    <div className="au-root">
      <nav style={{ padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="small" /></Link>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Link to="/dashboard" style={{ color: "var(--au-text-2)", textDecoration: "none", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><ArrowLeft size={14} /> Dashboard</Link>
          <ProfileMenu />
        </div>
      </nav>

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 12 }}>
          <TrendingUp size={24} style={{ color: "var(--au-accent-ink)" }} /> Trends &amp; Comparison
        </h1>
        <p style={{ color: "var(--au-muted)", marginBottom: 28, fontSize: 14 }}>Risk over time and scan-to-scan diffs across your targets</p>

        {series.length === 0 ? (
          <div className="au-glass au-pad-lg" style={{ textAlign: "center" }}>
            <ShieldCheck size={40} style={{ color: "var(--au-accent-ink)", marginBottom: 12 }} />
            <p style={{ color: "var(--au-muted)", fontSize: 15, marginBottom: 14 }}>No completed scans yet.</p>
            <Link to="/scan"><button className="au-pill au-pill-primary">Run a scan <ArrowRight size={16} /></button></Link>
          </div>
        ) : (
          <>
            <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
              <div className="au-stat-lbl" style={{ letterSpacing: 3, marginBottom: 16 }}>Risk Score Over Time</div>
              <RiskLine series={series} />
            </div>

            <div className="au-glass" style={{ overflow: "hidden", marginBottom: 20 }}>
              <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--au-hair)", fontSize: 14, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1 }}>TARGETS</div>
              <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 620 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.2fr", padding: "10px 22px", fontSize: 10, color: "var(--au-muted)", letterSpacing: 2, textTransform: "uppercase", borderBottom: "1px solid var(--au-hair)" }}>
                    <span>Target</span><span>Scans</span><span>Latest</span><span>Change</span><span>Compare</span>
                  </div>
                  {targets.map((t) => (
                    <div key={t.target} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.2fr", padding: "14px 22px", borderBottom: "1px solid var(--au-hair)", alignItems: "center" }}>
                      <span className="au-mono" style={{ fontSize: 13, color: "var(--au-text-2)", wordBreak: "break-all" }}>{t.target}</span>
                      <span style={{ fontSize: 13, color: "var(--au-muted)" }}>{t.scans}</span>
                      <span className="au-mono" style={{ fontSize: 18, fontWeight: 700, color: RC(t.latest_risk) }}>{t.latest_risk}</span>
                      <span><DeltaBadge delta={t.delta} /></span>
                      <span>
                        <button onClick={() => compare(t)} disabled={!t.previous_scan_id || cmpBusy}
                          className="au-pill au-pill-glass au-pill-sm"
                          aria-label={`Compare last two scans of ${t.target}`}
                          style={{ opacity: t.previous_scan_id ? 1 : 0.4, cursor: t.previous_scan_id ? "pointer" : "not-allowed", padding: "4px 10px", fontSize: 11 }}>
                          <GitCompare size={12} /> Compare
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {(cmpBusy || cmp) && (
              <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
                <div className="au-stat-lbl" style={{ letterSpacing: 2, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <GitCompare size={14} style={{ color: "var(--au-accent-ink)" }} /> Comparison — {cmpTarget}
                </div>
                {cmpBusy ? (
                  <div style={{ display: "flex", gap: 10, alignItems: "center", color: "var(--au-muted)", fontSize: 13 }}><Loader2 size={16} className="au-spin" /> Diffing…</div>
                ) : cmp && (
                  <>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
                      <div style={{ fontSize: 13, color: "var(--au-muted)" }}>
                        <span className="au-mono" style={{ color: RC(cmp.baseline.risk_score) }}>{cmp.baseline.risk_score}</span>
                        <span style={{ margin: "0 8px" }}>→</span>
                        <span className="au-mono" style={{ color: RC(cmp.current.risk_score), fontSize: 16, fontWeight: 700 }}>{cmp.current.risk_score}</span>
                        <span style={{ marginLeft: 10 }}><DeltaBadge delta={cmp.risk_delta} /></span>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--au-error)" }}>{cmp.summary.new} new</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--au-success)" }}>{cmp.summary.fixed} fixed</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--au-muted)" }}>{cmp.summary.persisting} persisting</span>
                      </div>
                    </div>
                    <DiffList title="New findings" color="var(--au-error)" items={cmp.new_findings} />
                    <DiffList title="Fixed findings" color="var(--au-success)" items={cmp.fixed_findings} />
                    <DiffList title="Still present" color="var(--au-muted)" items={cmp.persisting_findings} />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DiffList({ title, color, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>{title} ({items.length})</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--au-glass)", border: "1px solid var(--au-border)", borderLeft: `3px solid ${color}`, borderRadius: "var(--au-r-input)" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: SEVHEX[f.severity] || "var(--au-muted)", flex: "none" }} />
            <span style={{ fontSize: 13, color: "var(--au-text-2)" }}>{f.title}</span>
            <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--au-muted)", letterSpacing: 1, textTransform: "uppercase" }}>{f.severity}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
